// Fase 4 - Páginas complementares integradas com backend real.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench, History, Settings, AlertTriangle, ClipboardList, TrendingUp, ArrowLeft, Truck,
  Eye, Fuel, Circle, ClipboardCheck, DollarSign, Clock,
} from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';

const statusTone = (s) => ({
  'Aberta': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'Aguardando Aprovação': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'Em Andamento': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Pausada': 'bg-neutral-200 text-neutral-700',
  'Aguardando Peças': 'bg-amber-100 text-amber-800',
  'Finalizada': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Cancelada': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}[s] || 'bg-neutral-200 text-neutral-700');

// ============ Manutenção Corretiva ============
// OS filtradas por is_preventive=false ou tipo "Corretiva".
export function Corretivas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10, is_preventive: false };
      if (statusF !== 'all') params.status = statusF;
      const { data } = await api.get('/maintenance-orders', { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Erro ao carregar OS'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF]);

  return (
    <div>
      <PageHeader
        title="Manutenções Corretivas"
        subtitle="OS não planejadas (falhas, sinistros e defeitos)"
        onAction={() => navigate('/manutencao/os')}
        actionLabel="Nova OS corretiva"
      />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4 flex gap-3 items-center flex-wrap">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="corr-status-filter" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="Aberta">Aberta</SelectItem>
            <SelectItem value="Em Andamento">Em Andamento</SelectItem>
            <SelectItem value="Aguardando Peças">Aguardando Peças</SelectItem>
            <SelectItem value="Finalizada">Finalizada</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Nº OS</th>
              <th className="text-left px-4 py-3">Veículo</th>
              <th className="text-left px-4 py-3">Defeito</th>
              <th className="text-left px-4 py-3">Prioridade</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="corr-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={7} className="p-4"><TableSkeleton rows={5} cols={7} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={7}><EmptyState icon={AlertTriangle} title="Nenhuma OS corretiva" description="Crie uma OS marcando-a como corretiva." /></td></tr> :
              items.map(os => (
                <tr key={os.id} className="tr-hover">
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{os.number}</td>
                  <td className="px-4 py-3 font-mono text-xs">{os.vehicle_plate}</td>
                  <td className="px-4 py-3 text-xs max-w-xs truncate">{os.reported_defect || '-'}</td>
                  <td className="px-4 py-3 text-xs">{os.priority}</td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(os.os_status)}`}>{os.os_status}</span></td>
                  <td className="px-4 py-3 text-right font-medium text-xs">{formatCurrency((os.costs?.total) || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => navigate(`/manutencao/os/${os.id}`)}><Eye className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>
    </div>
  );
}

// ============ Histórico Técnico ============
// Seleção de veículo + timeline consolidada (OS, checklists, pneus, abastecimentos).
function VehicleSelector({ value, onChange, testid }) {
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/vehicles', { params: { limit: 500 } });
        setVehicles(data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);
  return (
    <Select value={value || 'none'} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full md:w-96">
        <SelectValue placeholder="Selecione um veículo..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— Selecione —</SelectItem>
        {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate} — {v.brand} {v.model}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const KIND_META = {
  os: { icon: Wrench, label: 'OS', tone: 'bg-emerald-500' },
  checklist: { icon: ClipboardCheck, label: 'Checklist', tone: 'bg-indigo-500' },
  fuel: { icon: Fuel, label: 'Abastecimento', tone: 'bg-amber-500' },
  tire: { icon: Circle, label: 'Pneu', tone: 'bg-purple-500' },
};

export function HistoricoTecnico() {
  const [vehicleId, setVehicleId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!vehicleId || vehicleId === 'none') { setEvents([]); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/maintenance-dashboard/vehicle/${vehicleId}/technical-history`);
        setEvents(data || []);
      } catch { toast.error('Erro ao carregar histórico'); } finally { setLoading(false); }
    })();
  }, [vehicleId]);

  return (
    <div>
      <PageHeader title="Histórico Técnico Consolidado" subtitle="Timeline unificada por veículo (OS, checklists, pneus, abastecimentos)" />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <VehicleSelector value={vehicleId} onChange={setVehicleId} testid="hist-vehicle" />
      </div>

      {!vehicleId || vehicleId === 'none' ? (
        <EmptyState icon={Truck} title="Selecione um veículo" description="Escolha um veículo acima para ver seu histórico técnico completo." />
      ) : loading ? (
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <TableSkeleton rows={5} cols={2} />
        </div>
      ) : events.length === 0 ? (
        <EmptyState icon={History} title="Sem eventos técnicos" description="Este veículo ainda não possui registros de OS, checklists, pneus ou abastecimentos." />
      ) : (
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <ol data-testid="hist-timeline" className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-4">
            {events.map((e, i) => {
              const meta = KIND_META[e.kind] || { icon: History, label: e.kind, tone: 'bg-neutral-400' };
              const Ico = meta.icon;
              const when = e.date ? new Date(e.date).toLocaleString('pt-BR') : '';
              return (
                <li key={i} className="pl-6 relative">
                  <span className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900 ${meta.tone}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 uppercase font-semibold flex items-center gap-1">
                      <Ico className="h-3 w-3" />{meta.label}
                    </span>
                    <span className="text-sm font-medium">{e.title}</span>
                    {typeof e.cost === 'number' && e.cost > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-brand/10 text-brand font-semibold">{formatCurrency(e.cost)}</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{when}{e.subtitle ? ` • ${e.subtitle}` : ''}</div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

// ============ Indicadores ============
export function Indicadores() {
  const [vehicleId, setVehicleId] = useState(null);
  const [ind, setInd] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!vehicleId || vehicleId === 'none') { setInd(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/maintenance-dashboard/vehicle/${vehicleId}/indicators`);
        setInd(data);
      } catch { toast.error('Erro ao carregar indicadores'); } finally { setLoading(false); }
    })();
  }, [vehicleId]);

  const Card = ({ icon: Icon, label, value, sub, tone = 'default', testid }) => {
    const toneMap = { default: 'text-brand', warn: 'text-amber-600', critical: 'text-red-600', success: 'text-emerald-600' };
    return (
      <div data-testid={testid} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-neutral-500 font-medium">{label}</p>
            <p className="text-2xl font-bold mt-2">{value ?? '-'}</p>
          </div>
          <div className={`h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center ${toneMap[tone]}`}><Icon className="h-5 w-5" /></div>
        </div>
        {sub && <p className="text-xs text-neutral-500">{sub}</p>}
      </div>
    );
  };

  const lastOsDate = ind?.last_os ? new Date(ind.last_os.opening_date || ind.last_os.created_at).toLocaleDateString('pt-BR') : null;

  return (
    <div>
      <PageHeader title="Indicadores por Veículo" subtitle="KPIs consolidados de manutenção e consumo" />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <VehicleSelector value={vehicleId} onChange={setVehicleId} testid="ind-vehicle" />
      </div>

      {!vehicleId || vehicleId === 'none' ? (
        <EmptyState icon={TrendingUp} title="Selecione um veículo" description="Escolha um veículo para visualizar seus indicadores." />
      ) : loading || !ind ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-32 rounded-xl shimmer" />)}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card icon={Wrench} label="OS total" value={ind.os_count} sub={`${ind.os_open} em aberto`} />
            <Card icon={DollarSign} label="Total gasto" value={formatCurrency(ind.total_spent)} />
            <Card icon={Clock} label="Última OS" value={lastOsDate || '-'} sub={ind.last_os?.number} />
            <Card icon={AlertTriangle} label="Próxima preventiva" value={ind.next_preventive?.name || '-'}
              sub={ind.next_preventive?.next?.days_left != null ? `em ${ind.next_preventive.next.days_left} dias` : ''}
              tone={ind.next_preventive?.next?.plan_status === 'overdue' ? 'critical' : ind.next_preventive?.next?.plan_status === 'due_soon' ? 'warn' : 'default'} />
            <Card icon={Fuel} label="Total abastecido" value={`${ind.fuel_summary?.total_liters || 0} L`} sub={formatCurrency(ind.fuel_summary?.total_value || 0)} />
            <Card icon={TrendingUp} label="Consumo médio" value={ind.fuel_summary?.avg_km_per_liter ? `${ind.fuel_summary.avg_km_per_liter} km/L` : '-'} tone="success" />
            <Card icon={Fuel} label="Abastecimentos" value={ind.fuel_summary?.count || 0} />
            <Card icon={ClipboardCheck} label="Último abastecim." value={ind.last_fuel ? `${ind.last_fuel.liters} L` : '-'}
              sub={ind.last_fuel ? new Date(ind.last_fuel.date).toLocaleDateString('pt-BR') : ''} />
          </div>

          {ind.last_os && (
            <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Wrench className="h-4 w-4" />Última OS</h3>
              <div className="text-sm grid grid-cols-1 md:grid-cols-4 gap-3">
                <div><span className="text-neutral-500 text-xs uppercase">Número</span><div className="font-mono">{ind.last_os.number}</div></div>
                <div><span className="text-neutral-500 text-xs uppercase">Status</span><div><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(ind.last_os.os_status)}`}>{ind.last_os.os_status}</span></div></div>
                <div><span className="text-neutral-500 text-xs uppercase">Tipo</span><div>{ind.last_os.maintenance_type_name || '-'}</div></div>
                <div><span className="text-neutral-500 text-xs uppercase">Total</span><div className="font-semibold">{formatCurrency(ind.last_os.costs?.total || 0)}</div></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Configurações ============
export function ManutencaoConfig() {
  const navigate = useNavigate();
  const cards = [
    { title: 'Tipos de manutenção', description: 'Cadastro de tipos (preventiva, corretiva, revisão…)', icon: Wrench, to: '/cadastros/maintenance-types' },
    { title: 'Categorias de manutenção', description: 'Grupos técnicos (motor, freios, elétrica…)', icon: ClipboardList, to: '/categorias' },
    { title: 'Templates de checklist', description: 'Modelos configuráveis com itens e criticidade', icon: ClipboardCheck, to: '/manutencao/checklists' },
    { title: 'Fornecedores / Oficinas', description: 'Cadastro central de fornecedores e oficinas', icon: Settings, to: '/cadastros/fornecedores' },
    { title: 'Combustíveis', description: 'Tipos de combustível utilizados na frota', icon: Fuel, to: '/cadastros/fuel-types' },
    { title: 'Postos', description: 'Postos de abastecimento parceiros', icon: Fuel, to: '/cadastros/postos-combustivel' },
    { title: 'Centros de custo', description: 'Centros de custo utilizados nas OS', icon: DollarSign, to: '/centros-custo' },
    { title: 'Tags', description: 'Etiquetas para classificar veículos e OS', icon: History, to: '/tags' },
  ];
  return (
    <div>
      <PageHeader title="Configurações de Manutenção" subtitle="Cadastros e templates que alimentam o módulo" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c, i) => (
          <button
            key={i}
            data-testid={`cfg-${c.to.replace(/\//g, '-')}`}
            onClick={() => navigate(c.to)}
            className="text-left rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 card-hover"
          >
            <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center mb-3"><c.icon className="h-5 w-5" /></div>
            <h3 className="font-semibold">{c.title}</h3>
            <p className="text-xs text-neutral-500 mt-1">{c.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ Relatórios ============
export function Relatorios() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/maintenance-reports');
        setReports(data || []);
      } catch { toast.error('Erro ao carregar relatórios'); } finally { setLoading(false); }
    })();
  }, []);

  const download = async (key, fmt) => {
    setBusy(`${key}-${fmt}`);
    try {
      const res = await api.get(`/maintenance-reports/${key}`, { params: { fmt }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${key}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch { toast.error('Erro ao exportar'); } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader title="Relatórios de Manutenção" subtitle="Exportações em CSV e Excel" />
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-xl shimmer" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {reports.map(r => (
            <div key={r.key} data-testid={`report-${r.key}`} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{r.name}</h3>
                  <p className="text-xs text-neutral-500 mt-1">{r.description}</p>
                </div>
                <FileBarChart />
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`export-${r.key}-csv`} size="sm" variant="outline" disabled={!!busy} onClick={() => download(r.key, 'csv')}>
                  {busy === `${r.key}-csv` ? 'Gerando…' : 'CSV'}
                </Button>
                <Button data-testid={`export-${r.key}-xlsx`} size="sm" className="bg-brand hover:bg-[#A81E23] text-white" disabled={!!busy} onClick={() => download(r.key, 'xlsx')}>
                  {busy === `${r.key}-xlsx` ? 'Gerando…' : 'Excel'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Ícone local (evita import redundante)
function FileBarChart() {
  return <TrendingUp className="h-5 w-5 text-neutral-400 shrink-0" />;
}
